import Add from '@mui/icons-material/Add';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from 'src/components/Button';
import { FormLabel } from 'src/components/FormLabel';
import { IconButton } from 'src/components/IconButton';
import { Stack } from 'src/components/Stack';
import { Tooltip } from 'src/components/Tooltip';
import { Typography } from 'src/components/Typography';
import { Coupon } from 'src/models/coupon/_types';
import { User } from 'src/models/user/_types';
import api from 'src/utils/ajax';

import { CouponAutocomplete } from './CouponAutocomplete';

interface AddCouponProps {
  onApplyCoupon: (coupon: { code: string } | undefined) => void;
  onRemoveCoupon: () => void;
  text?: string;
  showHideToggle?: boolean;
  couponType?: string;
  modelId?: string | number;
  modelType?: string | number;
  user?: User;
  openCouponModal?: (type: string) => void;
  toggleAddCoupon?: () => void;
  selectedCoupon?: Coupon;
}

export const AddCoupon: React.FC<AddCouponProps> = ({
  onApplyCoupon,
  onRemoveCoupon,
  text = 'Add Coupon',
  showHideToggle = true,
  couponType = 'purchase',
  modelId = '',
  modelType,
  user,
  openCouponModal,
  toggleAddCoupon,
  selectedCoupon,
}) => {
  const { t } = useTranslation('translation', { keyPrefix: 'coupon' });
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [couponLoading, setCouponLoading] = useState(false);

  const checkCoupon = async (coupon: Coupon | null) => {
    setSuccessMessage('');
    setErrorMessage('');

    if (coupon?.code) {
      setCouponLoading(true);
      try {
        const data = {
          coupon: coupon.code,
          type: couponType,
          user_id: user?.id,
          purchaseable: modelId
            ? {
                purchaseable_id: modelId,
                purchaseable_type: modelType,
              }
            : undefined,
        };

        const result = await api.get('/confirm-coupon', {
          params: data,
        });

        if (result?.data?.valid) {
          setSuccessMessage(result.data.message);
          onApplyCoupon(coupon);
        } else {
          setErrorMessage(result.data.message);
          onApplyCoupon(undefined);
        }
      } catch (error) {
        setErrorMessage(t('validationError'));
        onApplyCoupon(undefined);
      } finally {
        setCouponLoading(false);
      }
    } else {
      onRemoveCoupon();
    }
  };

  return (
    <Stack>
      <Stack direction="row" spacing={1} alignItems="center">
        <FormLabel>{text || t('title')}</FormLabel>
        {openCouponModal && (
          <Tooltip title={t('create')}>
            <IconButton onClick={() => openCouponModal(couponType)}>
              <Add fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Stack>
      <CouponAutocomplete
        value={selectedCoupon || null}
        text={''}
        loading={couponLoading}
        searchWithNoOptions={true}
        couponTypes={
          couponType === 'purchase'
            ? ['coupon_purchase']
            : ['coupon_subscription']
        }
        onChange={(coupon) => {
          checkCoupon(coupon);
        }}
      />
      {successMessage && (
        <Typography color="green">{successMessage}</Typography>
      )}
      {errorMessage && <Typography color="error">{errorMessage}</Typography>}
      {showHideToggle && toggleAddCoupon && (
        <Button onClick={toggleAddCoupon}>{t('options.noCoupon')}</Button>
      )}
    </Stack>
  );
};
